import React, { FC, Fragment, useEffect, useMemo, useState } from "react";
import SB from "@components/common/Sidebar";
import {
  CancelPolicyRule,
  Tour,
  TourByIdQuery,
  TourStatus,
} from "@graphql/types";
import Link from "next/link";
import { getDuration } from "@helpers/getDuration";
import { Trans, t } from "@helpers/translate";
import { InputDate } from "./InputDate";
import { InputPeople } from "./InputPeople";
import { DateTime } from "luxon";
import { getCurrencyConversion } from "@helpers/getCurrencyConversion";
import { useQuery } from "react-query";
import { graphql } from "@helpers/graphql";
import { useNotification } from "@helpers/hooks/useNotification";

import { useForm } from "react-hook-form";

import { InputStartTime } from "./InputStartTime";
import getCancellationTime from "@helpers/getCancellationTime";
import { useRouter } from "next/router";
import { Card } from "@components/ui/Card";
import Icon from "@components/ui/Icon";
import { Button } from "@components/ui/Button";
import { Rating } from "@components/ui/Rating";
import { Scarcity } from "@components/ui/Scarcity";
import { isValidDate } from "@helpers/date/check-is-validate-date";
import { useAuth } from "@helpers/hooks/useAuth";
import { ROUTES } from "@root/routes";
import { convertUrlParamsToQueryParam } from "../../../../routes";

type Props = {
  tour: NonNullable<TourByIdQuery["tourById"]>;
  onCheckOut?: (fileds: Fields) => void | undefined;
  isModal?: boolean;
};

const Header: FC<Props> = ({ tour }) => {
  return (
    <div className="flex flex-row">
      <h2 className="text-base font-semibold flex-1 truncate mr-2">
        {tour.title}
      </h2>
      <Card.Tags className="mb-0">
        {tour.city && <Card.Tag>{tour.city.name}</Card.Tag>}
        {tour.duration && <Card.Tag>{getDuration(tour.duration)}</Card.Tag>}
      </Card.Tags>
    </div>
  );
};

interface PriceProps extends Props {
  adults?: number;
  children?: number;
  infants?: number;
}

const Price: FC<PriceProps> = ({
  tour,
  adults = 2,
  children = 0,
  infants = 0,
}) => {
  const variables = { tour, adults, children, infants };
  const people = adults + children + infants;
  const { data, isLoading } = useQuery(["tourPrice", variables], () => {
    if (adults === 0 && children === 0 && infants === 0) {
      return undefined;
    }
    return graphql.tourPrice({
      id: tour.id,
      input: {
        adults,
        children,
        infants,
      },
    });
  });

  const tourPrice = data?.tourPrice || tour.price;

  const isPricePerGroup = tourPrice.model === "GROUP";
  return (
    <div className="text-center relative">
      <div className={isLoading ? "opacity-0" : "opacity-100"}>
        <div
          className={`text-3xl pb-2 font-semibold ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
          suppressHydrationWarning
        >
          {getCurrencyConversion(tourPrice.amount, tourPrice.currency)}
        </div>
        <div className={`text-sm ${isLoading ? "opacity-0" : "opacity-100"}`}>
          {isPricePerGroup && (
            <Trans id="tour.sidebar.price.per_group">per group</Trans>
          )}

          {!isPricePerGroup && (
            <>
              {getCurrencyConversion(
                tourPrice.amount / people,
                tourPrice.currency
              )}
              {` `}
              <Trans id="tour.sidebar.price.per_person">/ per person</Trans>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface freeOfChargeProps {
  rules: CancelPolicyRule[];
}

const FreeOfCharge: FC<freeOfChargeProps> = ({ rules }) => {
  const cancellationMessage = getCancellationTime(rules);

  if (cancellationMessage === "") {
    return <Fragment />;
  }

  return (
    <div className="pt-4 flex items-center justify-center">
      <Icon icon="checkCircle" size="sm" className="text-green-800 mr-1" />
      <Link
        href="/cancellation-policy"
        title={t({
          id: "common.link.title",
          message: "GoWithGuide - Private Tours & Local Tour Guides",
        })}
        className="hover:underline"
        target="_blank"
      >
        {cancellationMessage}
      </Link>
    </div>
  );
};

export interface Fields {
  date: Date | undefined;
  people: { adults: number; children: number; infants: number };
  time?: string;
  days_to_cancel: number;
}

export const Sidebar: FC<Props> = ({ tour, onCheckOut, isModal = false }) => {
  const router = useRouter();

  const numberPeopleDefault = useMemo(() => {
    if (tour.people.max < 2) {
      return 1;
    }
    return 2;
  }, [tour]);

  // Get the parameters from query url using effects
  // Since we are using GetStaticProps, query parameter will be coming after a second and
  // so default value will be used. So, to make sure query parameter is read properly,
  // Use effect and check if router.isReady and then ready the input and set it.

  useEffect(() => {
    const params = router.query;

    if (router.isReady) {
      setInput({
        people: {
          adults: params.adults ? Number(params.adults) : numberPeopleDefault,
          children: Number(params.children || 0),
          infants: Number(params.infants || 0),
        },
        date: params.date ? new Date(String(params.date)) : undefined,
        time: undefined,
        days_to_cancel:
          tour.cancelPolicyRules.length > 0
            ? tour.cancelPolicyRules[tour.cancelPolicyRules.length - 1].to + 1
            : 0,
      });
    }
  }, [router.isReady]);

  const [isSubmitted, onSubmitted] = useState(false);
  const notification = useNotification();

  const [input, setInput] = useState<Fields>({
    date: undefined,
    days_to_cancel: 0,
    people: { adults: numberPeopleDefault, children: 0, infants: 0 },
    time: undefined,
  });

  let dateError = false;
  let peopleError = false;
  let timeError = false;

  if (!tour) {
    return <Fragment />;
  }

  const bookParams = new URLSearchParams();

  if (input && input.date) {
    bookParams.set(
      "book_date",
      DateTime.fromJSDate(input.date).toFormat("yyyy-MM-dd")
    );
  }

  if (input) {
    if (tour.hasPriceRules) {
      bookParams.set("book_people", String(input.people.adults));

      if (input.people.children && input.people.children > 0) {
        bookParams.set("children", String(input.people.children));
      }

      if (input.people.infants && input.people.infants > 0) {
        bookParams.set("infant", String(input.people.infants));
      }
    } else {
      bookParams.set(
        "book_people",
        String(
          input.people.adults +
            (input.people.children || 0) +
            (input.people.infants || 0)
        )
      );
    }
  }

  const { handleSubmit } = useForm();

  const onSubmit = handleSubmit(() => {
    if (!input) {
      onSubmitted(true);
      return;
    }

    if (!input.date || input.people.adults === 0 || !input.time) {
      onSubmitted(true);
      return;
    }

    // Validate the selected time is not in the past
    if (!isValidTime(input)) {
      notification.error(
        t({
          id: "tour.sidebar.time.past.error",
          message:
            "Cannot book a tour with starting time in the past. Please choose a different starting time, or message the supplier by using the Message button.",
        })
      );
      return;
    }

    onCheckOut && onCheckOut(input);
  });

  //validation function -> if the meeting time is in the future
  const isValidTime = (input: Fields): boolean => {
    if (input.date && input.time) {
      // Get the supplier's time zone from the city data, otherwise fallback to the country timezone (default to UTC if not available)
      const supplierTimeZone =
        tour?.city?.timezone || tour?.city?.country?.timezone || "UTC";

      const inputDateISO = input.date.toISOString();

      // Combine the date with the input time to create the full meeting datetime in supplier's local time zone and then convert to UTC
      const meetingDateTimeLocal = DateTime.fromISO(inputDateISO, {
        zone: supplierTimeZone,
      }).set({
        hour: parseInt(input.time.split(":")[0]),
        minute: parseInt(input.time.split(":")[1]),
      });

      const meetingDateTimeUTC = meetingDateTimeLocal.toUTC();

      // Get the current time in UTC, rounded to the start of the minute (ignoring seconds)
      const currentUTC = DateTime.utc().startOf("minute");

      return meetingDateTimeUTC > currentUTC;
    }

    return false;
  };

  const isInvalidTour = (tour: Tour) => {
    return tour.status === TourStatus.Inactive || tour.supplier.isBan;
  };

  const variables = { tour };

  const { data } = useQuery(["tourCalendar", variables], () => {
    return graphql.tourCalendar({
      id: variables.tour.id,
    });
  });

  const displayName = tour.supplier.displayName;
  const peopleCount = input?.people.adults || 0;

  if (isSubmitted) {
    dateError = input?.date != null ? false : true;
    peopleError = peopleCount > 0 ? false : true;
    timeError = input?.time != null ? false : true;
  }

  const className = isModal
    ? "border-none bg-white shadow-none"
    : "hidden lg:block";

  const checkIsValidInputDate = () => {
    if (input?.date && isValidDate(input.date)) {
      return input?.date;
    }
    return undefined;
  };

  const { auth } = useAuth();

  const onTourRequest = () => {
    const params = new URLSearchParams();
    input?.date &&
      params.append(
        "date",
        DateTime.fromJSDate(input.date).toFormat("yyyy-MM-dd")
      );
    input?.time && params.append("startTime", input?.time);
    input?.people.adults && params.append("adults", `${input?.people.adults}`);
    input?.people.children &&
      params.append("children", `${input?.people.children}`);
    input?.people.infants &&
      params.append("infants", `${input?.people.infants}`);

    tour.id && params.append("tourId", tour.id.toString());

    if (!auth) {
      router.push(
        ROUTES.supplier(tour.supplier.id).message.signup(
          convertUrlParamsToQueryParam(params)
        )
      );
      return;
    }
    router.push(
      ROUTES.supplier(tour.supplier.id).message.form(
        convertUrlParamsToQueryParam(params)
      )
    );
  };

  return (
    <SB className={className}>
      <Header tour={tour} />
      <SB.Divider />

      <div>
        <InputDate
          showErrorView={dateError}
          defaultValue={checkIsValidInputDate()}
          calendar={data?.tourCalendar?.calendar || tour.calendar}
          showModal={dateError}
          onApply={(value) => setInput({ ...input, date: value })}
        />

        <InputPeople
          className="mt-2"
          showErrorView={peopleError}
          defaultValue={{
            adults: input.people.adults,
            children: input.people.children,
            infants: input.people.infants,
          }}
          onApply={(value) => setInput({ ...input, people: value })}
          minPeople={tour.people.min}
          maxPeople={tour.people.max}
        />
        <InputStartTime
          className="mt-2"
          showErrorView={timeError}
          defaultValue={input?.time}
          meetingTimeSlots={tour.meetingTime || []}
          showModal={!dateError && timeError}
          onApply={(value) => setInput({ ...input, time: value })}
        />
      </div>
      <SB.Divider />

      {input && <TourScarcity tour={tour as Tour} date={input.date} />}

      <div className="w-full bg-white mt-2 px-2">
        <Button
          className="w-full font-normal"
          disabled={isInvalidTour(tour as Tour)}
          rounded
          variant="blue"
          onClick={onTourRequest}
        >
          {t({
            id: "tour.message",
            message: `Message ${displayName}`,
            values: {
              displayName: `${displayName}`,
            },
          })}
        </Button>
        <Button
          className="w-full text-gray-700 font-normal mt-4"
          disabled={isInvalidTour(tour as Tour)}
          rounded
          variant="yellow2"
          onClick={onSubmit}
        >
          <Trans id="tour.reserve">Reserve</Trans>
        </Button>
      </div>

      <div className="flex flex-1 justify-center">
        {tour.supplier.stats.rating > 0 &&
          tour.supplier.stats.reviewsCount > 0 && (
            <Rating
              rate={tour.supplier.stats.rating}
              reviews={tour.supplier.stats.reviewsCount}
              alwaysShowDecimals={true}
              size="sm"
              variant="stars"
              className="mt-4"
              onClick={() => {
                router.push("#reviews");
              }}
            />
          )}
      </div>

      <div className="mt-4">
        <Price tour={tour} {...input.people} />
      </div>

      <FreeOfCharge rules={tour.cancelPolicyRules} />
    </SB>
  );
};

interface TourScarcityProps {
  tour: Tour;
  date?: Date;
}

const TourScarcity: FC<TourScarcityProps> = ({ tour, date }) => {
  const variables = { tour, date };

  const { data } = useQuery(["tourScarcity", variables], () => {
    return graphql.tourScarcity({
      id: variables.tour.id,
      input: {
        selectedDate: date ? DateTime.fromJSDate(date).toISODate() : null,
      },
    });
  });

  if (!data || !data.tourScarcity) {
    return <div></div>;
  }

  return (
    <Scarcity
      slot={data.tourScarcity?.slot || 0}
      month={data.tourScarcity?.month || ""}
    />
  );
};
